import { ReactNode, useEffect } from "react";
import toast from "react-hot-toast";
import { useAuth } from "react-oidc-context";
import WaveLoader from "./common/WaveLoader";
import PointIconLoader from "./common/PointIconLoader";

export const ProtectedRoutesProvider = ({ children }: { children: ReactNode }) => {
    const auth = useAuth();

    useEffect(() => {
        const renew = async () => {
            try {
                await auth.signinSilent();
            } catch (err) {
                console.error("Silent token renewal failed", err);
                toast.custom("Your session has expired. You'll be redirected to login...");
                window.location.href = "/portal/login" // * Fallback to interactive login.

            }
        };

        auth.events.addAccessTokenExpiring(renew);

        return () => {
            auth.events.removeAccessTokenExpiring(renew);
        };
    }, [auth]);

    useEffect(() => {
        const onUserSignedOut = () => {
            console.warn("User was signed out remotely");
            auth.removeUser();
            auth.revokeTokens();
            window.location.href = "/portal/login" // * Fallback to interactive login. 
        };
        auth.events.addUserSignedOut(onUserSignedOut);

        return () => {
            auth.events.removeUserSignedOut(onUserSignedOut);
        };
    }, [auth]);

    switch (auth.activeNavigator) {
        case "signinRedirect":
        case "signinSilent": {
            window.location.href = "/portal/login" // * Fallback to interactive login. 
            break;
        }
        case "signoutRedirect": {
            return (
                <div className="bg-background min-h-screen flex flex-col justify-center items-center">
                    <div className='w-full h-full flex flex-col justify-between items-center p-6 gap-6'>
                        <div className="w-full text-3xl lg:text-4xl text-center font-semibold text-primary">See you soon!</div>
                        <WaveLoader loaderClassname="h-48 w-48 lg:h-64 lg:w-64" />
                        <div className="text-xl lg:text-2xl text-center animate-pulse text-primary">Signing out...</div>
                    </div>
                </div>
            )
        }
    }

    if (auth.isLoading) {
        return (
            <div className="min-h-screen bg-background primary-font flex flex-col justify-center items-center">
                <PointIconLoader loaderText="Authenticating" />
            </div>
        )
    }

    if (!auth.isAuthenticated && auth.error == null) {
        window.location.href = "/portal/login" // * Fallback to interactive login. 
    }

    if (auth.error) {
        return <div>Error loading auth: {auth.error.message}</div>;
    }

    if (!auth.isAuthenticated) {
        window.location.href = "/portal/login" // * Fallback to interactive login. 
    }

    return <>{children}</>;
};